import React from 'react';
import noop from 'lodash/noop';
import { Aside } from '@atlaskit/navigation-system/layout/aside';
import { Banner as NewBanner } from '@atlaskit/navigation-system/layout/banner';
import { Main as NewMain } from '@atlaskit/navigation-system/layout/main';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { SideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { TopNav } from '@atlaskit/navigation-system/layout/top-nav';
import {
	BANNER_HEIGHT,
	LEFT_PANEL_WIDTH,
	LEFT_SIDEBAR_WIDTH,
	RIGHT_PANEL_WIDTH,
	RIGHT_SIDEBAR_WIDTH,
	TOP_NAVIGATION_HEIGHT,
} from '@atlaskit/navigation-system/legacy/css-variables';
import type { usePageLayoutResize as useLegacyPageLayoutResize } from '@atlaskit/page-layout/sidebar-resize-context';

// Common prop types used across multiple components
type BaseComponentProps = {
	testId?: string;
	children?: React.ReactNode;
};

type SizeableComponentProps = BaseComponentProps & {
	width?: number;
};

export type PageLayoutProps = BaseComponentProps & {
	isSideNavShortcutEnabled?: boolean;
};

export function PageLayout({
	testId,
	children,
	isSideNavShortcutEnabled,
}: PageLayoutProps): React.JSX.Element {
	return (
		<Root
			UNSAFE_dangerouslyHoistSlotSizes
			testId={testId}
			defaultSideNavCollapsed={undefined}
			isSideNavShortcutEnabled={isSideNavShortcutEnabled}
		>
			{children}
		</Root>
	);
}

type BannerProps = BaseComponentProps & {
	height?: number;
	id?: string;
};

export function Banner({ height, testId, id, children }: BannerProps): React.JSX.Element {
	return (
		<NewBanner height={height} testId={testId} id={id}>
			{children}
		</NewBanner>
	);
}

// eslint-disable-next-line jira/thunderstone/no-types-from-typeof -- CustomTheme is not exported from the platform package's public entry point; typeof is the only way to reference this type without depending on internal paths
type TopNavigationProps = BaseComponentProps & {
	height?: number;
	/**
	 * The Nav4 theme config.
	 */
	theme?: React.ComponentProps<typeof TopNav>['customTheme'];
};

export function TopNavigation({
	height,
	testId,
	theme,
	children,
}: TopNavigationProps): React.JSX.Element {
	return (
		<TopNav height={height} testId={testId} customTheme={theme}>
			{children}
		</TopNav>
	);
}

export function LeftSidebarWithoutResize({
	width,
	testId,
	children,
}: SizeableComponentProps): React.JSX.Element {
	return (
		<SideNav defaultWidth={width} testId={testId}>
			{children}
		</SideNav>
	);
}

type LeftSidebarProps = SizeableComponentProps & {
	collapsedState?: 'collapsed' | 'expanded';
};

export function LeftSidebar({
	collapsedState,
	width,
	testId,
	children,
}: LeftSidebarProps): React.JSX.Element {
	return (
		<SideNav defaultCollapsed={collapsedState === 'collapsed'} defaultWidth={width} testId={testId}>
			{children}
		</SideNav>
	);
}

export function Main({ testId, children }: BaseComponentProps): React.JSX.Element {
	return <NewMain testId={testId}>{children}</NewMain>;
}

export function RightSidebar({
	width,
	testId,
	children,
}: SizeableComponentProps): React.JSX.Element {
	return (
		<Aside defaultWidth={width} testId={testId}>
			{children}
		</Aside>
	);
}

export function RightPanel({
	width,
	testId,
	children,
	maxWidth,
}: SizeableComponentProps & {
	/**
	 * Opt-in override for the maximum width the panel can be resized to (e.g. `'70vw'`). When omitted,
	 * the panel keeps the default cap of half the viewport width.
	 */
	maxWidth?: React.ComponentProps<typeof Panel>['maxWidth'];
}): React.JSX.Element {
	return (
		<Panel defaultWidth={width} testId={testId} maxWidth={maxWidth}>
			{children}
		</Panel>
	);
}

// A mock value is needed to match the signature of the hook
const mockPageLayoutResize: ReturnType<typeof useLegacyPageLayoutResize> = {
	isLeftSidebarCollapsed: false,
	expandLeftSidebar: noop,
	collapseLeftSidebar: noop,
	leftSidebarState: {
		isFlyoutOpen: false,
		isResizing: false,
		isLeftSidebarCollapsed: false,
		leftSidebarWidth: 0,
		lastLeftSidebarWidth: 0,
		flyoutLockCount: 0,
		isFixed: true,
		hasInit: false,
	},
	toggleLeftSidebar: noop,
};

export const usePageLayoutResize: typeof useLegacyPageLayoutResize = () => mockPageLayoutResize;

export const UNSAFE_DO_NOT_USE_BANNER_HEIGHT: 'var(--bannerHeight, 0px)' = BANNER_HEIGHT;
export const UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH: 'var(--leftPanelWidth, 0px)' = LEFT_PANEL_WIDTH;
export const UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH: 'var(--leftSidebarWidth, 0px)' =
	LEFT_SIDEBAR_WIDTH;
export const UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH: 'var(--rightPanelWidth, 0px)' = RIGHT_PANEL_WIDTH;
export const UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH: 'var(--rightSidebarWidth, 0px)' =
	RIGHT_SIDEBAR_WIDTH;
export const UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT: 'var(--topNavigationHeight, 0px)' =
	TOP_NAVIGATION_HEIGHT;
